import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Building2, Search, Filter, Plus, Edit, Trash2, Mail, Key, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import { getCompanies, getCompanyUserInvitations, getFeatureKeys, deleteCompany, writeCompanyAudit } from '@/lib/firebase';
import AddCustomerModal from '@/components/add-customer-modal';
import EditCustomerModal from '@/components/edit-customer-modal';
import { logger } from '@/lib/logger';

interface Company {
  id: string;
  name: string;
  status: string;
  type?: string;
  trialExpiryDate?: any;
  createdAt?: any;
  updatedAt?: any;
  featureKeys?: string[];
  features?: Array<{ featureGroup: string; featureKeys: string[] }>;
}

interface FeatureKeyMeta {
  id: string;
  name: string;
  keyValue: string;
  enabled: boolean;
}

interface Invitation {
  id: string;
  companyId: string;
  email: string;
  status: string;
  role?: string;
  created?: any;
}

const PAGE_SIZE = 25;

export default function Customers() {
  // Initialise all filter/page state from URL so navigating back restores position
  const getUrlParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      searchTerm: params.get('search') || '',
      statusFilter: params.get('status') || 'all',
      typeFilter: params.get('type') || 'all',
      featureKeyFilter: params.get('feature') || 'all',
      currentPage: parseInt(params.get('page') || '1', 10),
    };
  };

  const initialParams = getUrlParams();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<Company[]>([]);
  const [currentPage, setCurrentPage] = useState(initialParams.currentPage);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(initialParams.searchTerm);
  const [statusFilter, setStatusFilter] = useState<string>(initialParams.statusFilter);
  const [typeFilter, setTypeFilter] = useState<string>(initialParams.typeFilter);
  const [featureKeyFilter, setFeatureKeyFilter] = useState<string>(initialParams.featureKeyFilter);
  const [addCustomerModalOpen, setAddCustomerModalOpen] = useState(false);
  const [editCustomerCompany, setEditCustomerCompany] = useState<Company | null>(null);
  const [featureKeysMeta, setFeatureKeysMeta] = useState<FeatureKeyMeta[]>([]);
  const { user } = useAuth();
  const { toast } = useToast();

  const statusOptions = ['all', ...Array.from(new Set(companies.map(company => company.status).filter(Boolean)))];
  const typeOptions = ['all', 'Customer', 'Partner'];

  useEffect(() => {
    getFeatureKeys()
      .then(keys => {
        const mapped = keys.map(d => {
          const name: string = d.name || '';
          return { ...d, keyValue: d.keyValue || name.trim().toLowerCase().replace(/\s+/g, '') } as FeatureKeyMeta;
        });
        setFeatureKeysMeta(mapped.filter(k => k.enabled));
      })
      .catch(err => logger.error('Error loading feature keys meta:', err));
  }, []);

  useEffect(() => {
    if (!user) return;

    const loadCompanies = async () => {
      try {
        logger.log('🏢 Loading companies...');
        const all = await getCompanies();
        const nonTrial = all.filter((c: any) => !c.trialExpiryDate) as Company[];
        logger.log('📋 Companies loaded (non-trial):', nonTrial.length);
        setCompanies(nonTrial);
      } catch (error: any) {
        logger.error('❌ Error loading companies:', error);
        toast({ title: "Failed to load customers", description: error.message, variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };

    const loadInvitations = async () => {
      try {
        const data = await getCompanyUserInvitations();
        setInvitations(data as Invitation[]);
      } catch (error: any) {
        logger.error('❌ Error loading invitations:', error);
      }
    };

    loadCompanies();
    loadInvitations();
    const timer = setInterval(() => { loadCompanies(); loadInvitations(); }, 15_000);
    return () => clearInterval(timer);
  }, [user, toast]);

  const hasFeatureKey = (company: Company, featureKeyDocId: string): boolean => {
    // New format: check features array by doc ID
    if (company.features && company.features.length > 0) {
      return company.features.some(fg => fg.featureKeys.includes(featureKeyDocId));
    }
    // Old format: company.featureKeys is a string[] of keyValues — match by keyValue
    if (Array.isArray(company.featureKeys) && company.featureKeys.length > 0) {
      const meta = featureKeysMeta.find(fk => fk.id === featureKeyDocId);
      return meta ? company.featureKeys.includes(meta.keyValue) : false;
    }
    return false;
  };

  // Sync filter/page state back to URL so navigating away and back preserves position
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (typeFilter !== 'all') params.set('type', typeFilter);
    if (featureKeyFilter !== 'all') params.set('feature', featureKeyFilter);
    if (currentPage > 1) params.set('page', currentPage.toString());
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState({}, '', newUrl);
  }, [searchTerm, statusFilter, typeFilter, featureKeyFilter, currentPage]);

  useEffect(() => {
    let filtered = companies;

    if (searchTerm.trim()) {
      filtered = filtered.filter(company =>
        company.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(company => company.status === statusFilter);
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(company => company.type === typeFilter);
    }

    if (featureKeyFilter !== 'all') {
      filtered = filtered.filter(company => hasFeatureKey(company, featureKeyFilter));
    }

    setFilteredCompanies(filtered);
  }, [companies, searchTerm, statusFilter, typeFilter, featureKeyFilter]);

  const getCompanyInvitations = (companyId: string): Invitation[] => {
    return invitations.filter(inv => inv.companyId === companyId);
  };

  const getEnabledFeatureNames = (company: Company): string[] => {
    if (company.features && company.features.length > 0) {
      // New format: look up names by doc ID
      const enabledIds = new Set(company.features.flatMap(fg => fg.featureKeys));
      return featureKeysMeta.filter(fk => enabledIds.has(fk.id)).map(fk => fk.name);
    }
    if (Array.isArray(company.featureKeys) && company.featureKeys.length > 0) {
      // Old format: keyValues
      return featureKeysMeta.filter(fk => company.featureKeys!.includes(fk.keyValue)).map(fk => fk.name);
    }
    return [];
  };

  const handleDeleteCompany = async (companyId: string, companyName: string) => {
    if (!confirm(`Are you sure you want to delete "${companyName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteCompany(companyId);
      await writeCompanyAudit(companyId, companyName, 'deleted', user?.email ?? 'unknown', user?.uid ?? '');
      toast({
        title: "Customer deleted",
        description: `${companyName} has been deleted successfully.`,
      });
    } catch (error: any) {
      logger.error('Error deleting company:', error);
      toast({
        title: "Failed to delete customer",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'suspended':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'partner':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'customer':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getInvitationStatusColor = (status: string) => {
    switch ((status || '').toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'sent':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-center text-slate-600">Loading customers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Building2 className="h-6 w-6" />
              Customers / Partners
            </h1>
            <p className="text-slate-600 mt-1">
              Manage your customer companies and their status
            </p>
          </div>
          <Button
            className="flex items-center gap-2"
            onClick={() => setAddCustomerModalOpen(true)}
            data-testid="button-add-customer"
          >
            <Plus className="h-4 w-4" />
            Add Customer / Partner
          </Button>
        </div>

        {/* Search and Filter Controls */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Search & Filter</CardTitle>
            <CardDescription>
              Find customers by name and filter by status
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
                  <Input
                    placeholder="Search by company name..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    className="pl-10"
                    data-testid="input-search-customers"
                  />
                </div>
              </div>
              <div className="w-full sm:w-48">
                <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setCurrentPage(1); }}>
                  <SelectTrigger data-testid="select-type-filter">
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    {typeOptions.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type === 'all' ? 'All Types' : type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full sm:w-48">
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
                  <SelectTrigger data-testid="select-status-filter">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status === 'all' ? 'All Statuses' : status.charAt(0).toUpperCase() + status.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {featureKeysMeta.length > 0 && (
                <div className="w-full sm:w-52">
                  <Select value={featureKeyFilter} onValueChange={(v) => { setFeatureKeyFilter(v); setCurrentPage(1); }}>
                    <SelectTrigger data-testid="select-feature-key-filter">
                      <SelectValue placeholder="Filter by feature" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Features</SelectItem>
                      {featureKeysMeta.map((fk) => (
                        <SelectItem key={fk.id} value={fk.id}>
                          {fk.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Results Summary */}
        <div className="flex items-center gap-4 text-sm text-slate-600">
          <span>
            {filteredCompanies.length === 0
              ? `0 of ${companies.length} customers`
              : `Showing ${Math.min((currentPage - 1) * PAGE_SIZE + 1, filteredCompanies.length)}–${Math.min(currentPage * PAGE_SIZE, filteredCompanies.length)} of ${filteredCompanies.length} customers`}
          </span>
          {searchTerm && (
            <Badge variant="secondary" className="gap-1">
              <Search className="h-3 w-3" />
              "{searchTerm}"
            </Badge>
          )}
          {typeFilter !== 'all' && (
            <Badge variant="secondary" className="gap-1">
              <Filter className="h-3 w-3" />
              {typeFilter}
            </Badge>
          )}
          {statusFilter !== 'all' && (
            <Badge variant="secondary" className="gap-1">
              <Filter className="h-3 w-3" />
              {statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
            </Badge>
          )}
          {featureKeyFilter !== 'all' && (
            <Badge variant="secondary" className="gap-1">
              <Key className="h-3 w-3" />
              {featureKeysMeta.find(fk => fk.id === featureKeyFilter)?.name ?? featureKeyFilter}
            </Badge>
          )}
        </div>

        {/* Companies Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Customers / Partners</CardTitle>
            <CardDescription>
              List of all customers and partners in the system
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredCompanies.length === 0 ? (
              <div className="text-center py-12">
                <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  {companies.length === 0 ? 'No customers found' : 'No matching customers'}
                </h3>
                <p className="text-slate-600 mb-4">
                  {companies.length === 0
                    ? 'Get started by adding your first customer company.'
                    : 'Try adjusting your search terms or filters.'}
                </p>
                {companies.length === 0 && (
                  <Button
                    className="flex items-center gap-2"
                    onClick={() => setAddCustomerModalOpen(true)}
                    data-testid="button-add-first-customer"
                  >
                    <Plus className="h-4 w-4" />
                    Add First Customer
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Company Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Features</TableHead>
                      <TableHead>Admin Invitation</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCompanies.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((company, index) => {
                      const companyInvitations = getCompanyInvitations(company.id);
                      const adminInvitations = companyInvitations.filter(
                        inv => (inv.role || '').toLowerCase() === 'administrator'
                      );
                      const latestAdminInvitation = adminInvitations[0] ?? companyInvitations[0] ?? null;

                      return (
                        <TableRow key={company.id || `company-${index}`} data-testid={`row-customer-${company.id}`}>
                          <TableCell>
                            <div className="font-medium">{company.name || 'Unnamed Company'}</div>
                          </TableCell>
                          <TableCell>
                            <Badge className={getTypeColor(company.type || '')}>
                              {company.type || 'Customer'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(company.status)}>
                              {company.status || 'Unknown'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const enabledFeatures = getEnabledFeatureNames(company);
                              if (enabledFeatures.length === 0) {
                                return <span className="text-slate-400 text-sm">—</span>;
                              }
                              return (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button className="flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900">
                                        <Key className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                                        <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 cursor-pointer">
                                          {enabledFeatures.length} {enabledFeatures.length === 1 ? 'feature' : 'features'}
                                        </Badge>
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="bottom" className="max-w-[220px]">
                                      <p className="font-medium mb-1 text-xs">Enabled features:</p>
                                      <ul className="space-y-0.5">
                                        {enabledFeatures.map(name => (
                                          <li key={name} className="text-xs">• {name}</li>
                                        ))}
                                      </ul>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            {latestAdminInvitation ? (
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-1.5">
                                  <Mail className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                                  <span className="text-sm text-slate-700 truncate max-w-[180px]">
                                    {latestAdminInvitation.email}
                                  </span>
                                </div>
                                <Badge className={`w-fit text-xs ${getInvitationStatusColor(latestAdminInvitation.status)}`}>
                                  {latestAdminInvitation.status || 'Pending'}
                                </Badge>
                              </div>
                            ) : (
                              <span className="text-slate-400 text-sm">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => setEditCustomerCompany(company)}
                                data-testid={`button-edit-${company.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteCompany(company.id, company.name)}
                                data-testid={`button-delete-${company.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {Math.ceil(filteredCompanies.length / PAGE_SIZE) > 1 && (
                  <div className="flex items-center justify-between border-t border-slate-200 pt-4 mt-2">
                    <span className="text-sm text-slate-600">
                      Page {currentPage} of {Math.ceil(filteredCompanies.length / PAGE_SIZE)}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="h-8 w-8 p-0"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      {Array.from({ length: Math.ceil(filteredCompanies.length / PAGE_SIZE) }, (_, i) => i + 1)
                        .filter(p => p === 1 || p === Math.ceil(filteredCompanies.length / PAGE_SIZE) || Math.abs(p - currentPage) <= 2)
                        .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                          if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('...');
                          acc.push(p);
                          return acc;
                        }, [])
                        .map((p, idx) =>
                          p === '...' ? (
                            <span key={`ellipsis-${idx}`} className="text-sm text-slate-400 px-1">…</span>
                          ) : (
                            <Button
                              key={p}
                              variant={p === currentPage ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setCurrentPage(p as number)}
                              className="h-8 w-8 p-0 text-sm"
                            >
                              {p}
                            </Button>
                          )
                        )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredCompanies.length / PAGE_SIZE), p + 1))}
                        disabled={currentPage === Math.ceil(filteredCompanies.length / PAGE_SIZE)}
                        className="h-8 w-8 p-0"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Customer Modal */}
        <AddCustomerModal
          isOpen={addCustomerModalOpen}
          onClose={() => setAddCustomerModalOpen(false)}
        />

        {/* Edit Customer Modal */}
        {editCustomerCompany && (
          <EditCustomerModal
            isOpen={!!editCustomerCompany}
            onClose={() => setEditCustomerCompany(null)}
            company={editCustomerCompany}
          />
        )}
      </div>
    </div>
  );
}
